import styled from "@emotion/styled";
import React, { LegacyRef } from "react";
import { CardProps, DeviceInfo } from "types";
import WidgetBox from "../WidgetBox";
import LDSUOverviewReading from "./LDSUOverviewReading";
import LDSUReading from "./LDSUReading";
import MultiSensorCard from "./MultiSensor";
import SingleCard from "./SingleSensor";
import {
    getWidgetTypeFromChartSource,
    WidgetType,
} from "../../utils/widgetConfig";

const CardContainer = styled.div`
    padding: 16px 24px 16px 18px;
    color: #fff;
    min-height: 210px;
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    grid-area: header;
`;

const CardContent = styled.div``;

const CardTitle = styled.div`
    font-size: 1.1rem;
    font-weight: 500;
    line-height: 1;
`;

const SensorContent = styled.div`
    display: flex;
    align-items: center;
`;

const IconCircle = styled.div<{ size?: number }>`
    width: ${({ size = 46 }) => size}px;
    height: ${({ size = 46 }) => size}px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 18px;

    > span {
        width:  ${({ size = 46 }) => size - 0.5}px;
        height: ${({ size = 46 }) => size - 0.5}px;
        > svg {
            width: 100%;
            height: 100%;
        }
    }
`;

const SensorInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const SensorLabel = styled.div<{ fontSize?: number | string }>`
    font-size: ${({ fontSize }) => fontSize ?? "18"}px;
    font-weight: 600;
    line-height: 1;
`;

const SensorType = styled.div<{ fontSize?: number | string }>`
    font-size: ${({ fontSize }) => fontSize ?? "14"}px;
    color: #828fab;
    font-weight: 400;
`;

const ReadingTimeText = styled(SensorType) <{ fontSize?: number | string }>`
    float: right;
`;

const DESIGN_WIDTH = 436;
const DESIGN_FONTSIZE = 18;
export interface CardPropsWithChildren extends CardProps {
    children?: React.ReactNode;
    ref?: LegacyRef<any>;
    sensorDetails: DeviceInfo[];
}

/**
 * Renders a card component based on the provided panel properties and visibility state.
 * Fetches device information asynchronously and displays either a multi or single card
 * depending on the source type.
 *
 * @param {CardPropsWithChildren} props - The properties for the card component.
 * @returns {JSX.Element} The rendered card component.
 * @throws {Error} Throws an error if fetching device information fails.
 */
const Card = (props: CardPropsWithChildren) => {
    const {
        panel,
        panel: { uuid: panelId, source, chart, name },
        visibleRef,
        zoomLevel,
        sensorDetails,
    } = props;


    const widgetType = getWidgetTypeFromChartSource(chart, source);
    const isLdsuOverviewReadings =
        widgetType === WidgetType.LDSU_CARD_OVERVIEW_READINGS;
    const isLdsuReadings = widgetType === WidgetType.LDSU_CARD_READINGS;
    const isMultiple =
        source.startsWith("MULTIPLE");

    const renderCard = () => {

        if (isLdsuReadings) {
            return (
                <LDSUReading
                    devices={sensorDetails ?? []}
                    widgetId={panelId}
                    panel={panel}
                />
            );
        }
        if (isLdsuOverviewReadings) {
            return (
                <LDSUOverviewReading
                    devices={sensorDetails ?? []}
                    widgetId={panelId}
                    panel={panel}
                />
            );
        }
        if (isMultiple) {
            return (
                <MultiSensorCard
                    devices={sensorDetails ?? []}
                    widgetId={panelId}
                    panel={panel}
                />
            );
        }
        return (
            <SingleCard
                device={sensorDetails?.[0] ?? {}}
                widgetId={panelId}
                chartType={chart}
            />
        );
    };

    return (
        <WidgetBox
            ref={visibleRef}
            className={
                zoomLevel <= 0.5
                    ? "widget-box w-100 h-30vh"
                    : "widget-box w-100"
            }
        >
            <div className="widget">
                <CardContainer>
                    <CardHeader className="header mb-3 mb-md-4">
                        <CardTitle className="text-truncate" title={name}>
                            {name}
                        </CardTitle>
                    </CardHeader>

                    <CardContent
                        className={`${isMultiple ? "" : "mt-md-auto flex-fill"
                            }`}
                    >
                        {renderCard()}
                    </CardContent>
                </CardContainer>
            </div>
        </WidgetBox>
    );
};

export default Card;

export {
    DESIGN_FONTSIZE,
    DESIGN_WIDTH,
    IconCircle,
    SensorContent,
    SensorInfo,
    SensorLabel,
    SensorType,
    ReadingTimeText,
};
